import React from 'react';
import { FileCheck, Trash2, Clock, Building2, User, CheckCircle, XCircle, Trophy, Medal } from 'lucide-react';
import type { Interview } from './index';

interface InterviewHistoryProps {
  interviews: Interview[];
  onNewInterview: () => void;
  onViewInterview: (interview: Interview) => void;
  onRemoveInterview: (date: string) => void;
}

const calculateMatchCategory = (skills: Interview['skills']) => {
  const totalSkills = skills.length;
  const evaluations = skills.reduce((acc, skill) => {
    if (!skill.evaluation) return acc;
    acc[skill.evaluation] = (acc[skill.evaluation] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const notMetPercentage = (evaluations.not_met || 0) / totalSkills * 100;
  const meetsOrExceedsPercentage = ((evaluations.fully_met || 0) + (evaluations.exceeds || 0)) / totalSkills * 100;
  const meetsPercentage = (evaluations.fully_met || 0) / totalSkills * 100;
  const partialOrBetterPercentage = ((evaluations.partially_met || 0) + (evaluations.fully_met || 0) + (evaluations.exceeds || 0)) / totalSkills * 100;

  if (meetsOrExceedsPercentage >= 70) {
    return {
      label: 'Gold Match 🏆',
      color: 'bg-yellow-50 text-yellow-700',
      icon: Trophy,
    };
  }
  if (meetsPercentage >= 50 && notMetPercentage <= 20) {
    return {
      label: 'Silver Match 🥈',
      color: 'bg-gray-50 text-gray-700',
      icon: Medal,
    };
  }
  if (partialOrBetterPercentage >= 30) {
    return {
      label: 'Bronze Match 🥉',
      color: 'bg-orange-50 text-orange-700',
      icon: Medal,
    };
  }
  return {
    label: 'No Match ❌',
    color: 'bg-red-50 text-red-700',
    icon: XCircle,
  };
};

export const InterviewHistory: React.FC<InterviewHistoryProps> = ({
  interviews,
  onNewInterview,
  onViewInterview,
  onRemoveInterview,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Histórico de Entrevistas
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Últimas {interviews.length} entrevistas realizadas
          </p>
        </div>

        <button
          onClick={onNewInterview}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
        >
          <FileCheck className="w-4 h-4" />
          Nova Entrevista
        </button>
      </div>

      {interviews.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Nenhuma entrevista realizada ainda</p>
          <button
            onClick={onNewInterview}
            className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
          >
            Realizar primeira entrevista
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {interviews.map((interview) => {
            const matchCategory = calculateMatchCategory(interview.skills);
            const MatchIcon = matchCategory.icon;

            return (
              <div
                key={interview.date}
                className="bg-white rounded-lg shadow-sm border border-gray-100 hover:border-blue-200 transition-colors"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <button
                      onClick={() => onViewInterview(interview)}
                      className="flex-1 text-left"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-medium text-gray-900">
                          {interview.position}
                        </h3>
                      </div>

                      <div className="flex items-center gap-2 mb-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${matchCategory.color}`}>
                          <MatchIcon className="w-3 h-3" />
                          {matchCategory.label}
                        </span>

                        {interview.recommendation && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                            interview.recommendation === 'approve'
                              ? 'bg-green-50 text-green-700'
                              : 'bg-red-50 text-red-700'
                          }`}>
                            {interview.recommendation === 'approve' ? (
                              <>
                                <CheckCircle className="w-3 h-3" />
                                Aprovado
                              </>
                            ) : (
                              <>
                                <XCircle className="w-3 h-3" />
                                Reprovado
                              </>
                            )}
                          </span>
                        )}
                      </div>

                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <User className="w-4 h-4" />
                          <span>{interview.candidateName}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Building2 className="w-4 h-4" />
                          <span>{interview.interviewer}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-500">
                          <Clock className="w-4 h-4" />
                          <span>
                            {new Date(interview.interviewDate).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => onRemoveInterview(interview.date)}
                      className="text-gray-400 hover:text-red-600 transition-colors p-1"
                      title="Remover do histórico"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="text-sm text-amber-800">
            <p>
              O histórico é armazenado localmente no seu navegador (localStorage) e mantém os últimos 10 registros.
              Ao limpar os dados do navegador, o histórico será perdido.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};